import {
  uploadRides,
  generateFleet,
  calculateTimeWindowsWithUserAuth,
  createProblem,
  createScenario,
  runSimulation,
  getTaskStatus,
  createManyVehicles,
  createBulkRides,
  solveProblem,
  getSolution,
  calculateTimeWindows
} from './api';
import type { SimulationConfig } from '../../types/simulation';
import { pashaRunSettingsFromRunBulk } from './clusteringDefaults';
import { loggingService } from '../logging/loggingService';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { extractServiceDate, combineDateWithTime, extractTimeFromDateTime, isValidTimeFormat } from '../../utils/dateTimeUtils';
import { registerShiftLabel, registerRouteMeta, clearShiftLabelRegistry, encodeVehicleBrandLabel } from './shiftLabelRegistry';

// `mapFleetToApiFleetSettings` accepts two shift/break input shapes — UI
// format (HH:mm strings on `start`/`end`) and API format (full datetimes on
// `start_time`/`end_time`). The UI-format path copies the bare time straight
// into `shift.start_time`, which then collides with `extractTimeFromDateTime`
// downstream. Normalising here keeps the existing dual-format contract
// without changing the shared datetime utility's strict semantics.
function normalizeTimeForFleetMapping(value: string): string {
  if (isValidTimeFormat(value)) {
    return value;
  }
  return extractTimeFromDateTime(value);
}

// Interface definitions for orchestrator workflows
export interface SimulationWorkflowInput {
  simulationConfig: any;
  vehicles: any[];
  rides: any[];
}

export interface SimulationWorkflowResult {
  problemId?: string;
  resultsId?: string;
  solution?: any;
  error?: string;
}

export interface ChainedSimulationWorkflowInput {
  scenarioName: string;
  timeWindowSettings: any;
  rides: any[];
  vehicles: any[];
  problemConfig: any;
  simulationTimezone?: string;
  fleet?: any; // Optional full fleet object to preserve metadata
  /** Forwarded to POST /pasha_bulka_simulation/run/ as matcher_flags; clustering only if enabled */
  run_bulk_simulation?: SimulationConfig['run_bulk_simulation'];
}

export interface ChainedSimulationWorkflowResult {
  scenarioId?: string;
  timeWindowId?: string;
  ridesBatchId?: string;
  vehiclesBatchId?: string;
  problemId?: string;
  taskId?: string;
  results?: any;
  error?: string;
}

/**
 * Direct simulation workflow using conductor API
 * This workflow creates a problem, adds vehicles and rides directly, then solves
 */
export async function runSimulationWorkflow({ 
  simulationConfig, 
  vehicles, 
  rides 
}: SimulationWorkflowInput): Promise<SimulationWorkflowResult> {
  try {
    loggingService.debug('🚀 Starting direct simulation workflow...');

    // 1. Create Problem
    loggingService.debug('Step 1: Creating problem...');
    const { problem_id } = await createProblem(simulationConfig);
    loggingService.debug('✅ Problem created', { category: 'SimulationOrchestrator', problem_id });

    // 2. Bulk Create Vehicles
    loggingService.debug('Step 2: Creating vehicles...');
    await createManyVehicles(vehicles, problem_id);
    loggingService.debug('✅ Vehicles created');

    // 3. Bulk Create Rides
    loggingService.debug('Step 3: Creating rides...');
    await createBulkRides(rides, problem_id);
    loggingService.debug('✅ Rides created');

    // 4. Solve Problem
    loggingService.debug('Step 4: Solving problem...');
    const { results_id } = await solveProblem(problem_id);
    loggingService.debug('✅ Problem solving started', { category: 'SimulationOrchestrator', results_id });

    // 5. Poll for Results
    loggingService.debug('Step 5: Polling for results...');
    let solution = null;
    let attempts = 0;
    const maxAttempts = 15;
    
    while (attempts < maxAttempts) {
      loggingService.debug(`Polling for solution (attempt ${attempts + 1})...`);
      const res = await getSolution(results_id);
      
      if (res && res.status === 'complete') {
        solution = res;
        break;
      }
      
      await new Promise(r => setTimeout(r, 2000));
      attempts++;
    }
    
    if (!solution) {
      throw new Error('Timed out waiting for simulation results');
    }
    
    loggingService.debug('✅ Simulation complete!');
    return { 
      problemId: problem_id, 
      resultsId: results_id, 
      solution 
    };
    
  } catch (error: any) {
    loggingService.error('❌ Simulation workflow failed', { category: 'SimulationOrchestrator', error });
    return { error: error.message || 'Unknown error' };
  }
}

/**
 * Chained simulation workflow using data generator APIs
 * This workflow uploads rides, generates fleet, calculates time windows, creates scenario, then runs simulation
 */
export async function runChainedSimulationWorkflow({
  scenarioName,
  timeWindowSettings,
  rides,
  vehicles,
  problemConfig,
  simulationTimezone,
  fleet,
  run_bulk_simulation,
}: ChainedSimulationWorkflowInput): Promise<ChainedSimulationWorkflowResult> {
  try {
    loggingService.debug('🚀 Starting chained simulation workflow...');

    // Reset the per-run shift-label registry before the first
    // `mapFleetToApiFleetSettings` call re-registers labels. The registry is a
    // module-level Map keyed by UUID; without this reset, labels from prior
    // simulation runs in the same browser session would leak and could shadow
    // current-run lookups when ids accidentally collide.
    clearShiftLabelRegistry();

    // 1. Upload Rides
    loggingService.debug('Step 1: Uploading rides...');
    const ridesRes = await uploadRides(rides, scenarioName);
    const ridesBatchId = ridesRes.batch_id;
    loggingService.debug('✅ Rides uploaded', { category: 'SimulationOrchestrator', ridesBatchId });

    // 2. Generate Fleet
    loggingService.debug('Step 2: Generating fleet...');
    // Transform fleet data to the correct format
    // Pass fleet object with metadata if available, otherwise construct from vehicles
    const fleetObject = fleet 
      ? { vehicles, uatMetadata: fleet.uatMetadata }
      : { vehicles };
    const transformedFleet = mapFleetToApiFleetSettings(fleetObject, [], rides[0]?.request_time);
    const fleetRes = await generateFleet(transformedFleet, scenarioName);
    const fleetId = fleetRes.fleet_id;
    loggingService.debug('✅ Fleet generated', { category: 'SimulationOrchestrator', fleetId });

    // 3. Calculate Time Windows
    loggingService.debug('Step 3: Calculating time windows...');
    const timeWindowConfig = {
      ...timeWindowSettings,
      ride_settings: {
        ...timeWindowSettings.ride_settings,
        batch_id: ridesBatchId
      }
    };
    const timeWindowRes = await calculateTimeWindowsWithUserAuth(timeWindowConfig, `${scenarioName}-time-window`);
    const timeWindowTaskId = timeWindowRes.time_window_batch_id;
    loggingService.debug('✅ Time window calculation started', { category: 'SimulationOrchestrator', timeWindowTaskId });

    // 4. Poll for time window completion
    loggingService.debug('Step 4: Waiting for time window calculation...');
    const timeWindowResult = await pollTimeWindowCalculation(timeWindowTaskId);
    const timeWindowBatchId = timeWindowResult.time_window_batch_id;
    loggingService.debug('✅ Time windows calculated', { category: 'SimulationOrchestrator', timeWindowBatchId });

    // 5. Create Problem Config
    loggingService.debug('Step 5: Creating problem config...');
    const problemConfigRes = await createProblem(problemConfig);
    const problemConfigId = problemConfigRes.problem_id;
    loggingService.debug('✅ Problem config created', { category: 'SimulationOrchestrator', problemConfigId });

    // 6. Create Scenario
    loggingService.debug('Step 6: Creating scenario...');
    const scenarioPayload = {
      scenario_name: scenarioName,
      ride_batch_id: ridesBatchId,
      time_window_batch_id: timeWindowBatchId,
      fleet_id: fleetId,
      problem_config_id: problemConfigId,
      timezone: simulationTimezone || fleet?.uatMetadata?.timezone || 'UTC',
    };
    
    const scenarioRes = await createScenario(scenarioPayload);
    const scenarioId = scenarioRes.scenario_id;
    loggingService.debug('✅ Scenario created', { category: 'SimulationOrchestrator', scenarioId });

    // 7. Run Simulation
    loggingService.debug('Step 7: Running simulation...');
    const simulationRes = await runSimulation(
      scenarioId,
      'pasha_bulka',
      pashaRunSettingsFromRunBulk(run_bulk_simulation)
    );
    const taskId = simulationRes.task_id;
    loggingService.debug('✅ Simulation started', { category: 'SimulationOrchestrator', taskId });

    // 8. Poll for simulation results
    loggingService.debug('Step 8: Waiting for simulation results...');
    let results = null;
    let attempts = 0;
    const maxAttempts = 30; // 10 minutes max
    
    while (attempts < maxAttempts) {
      loggingService.debug(`Polling for simulation results (attempt ${attempts + 1})...`);
      const taskStatus = await getTaskStatus(taskId);
      
      // Handle string parsing for chained workflow
      let parsedTaskStatus = taskStatus;
      if (typeof taskStatus === 'string') {
        try {
          parsedTaskStatus = JSON.parse(taskStatus);
        } catch (parseError) {
          loggingService.error('❌ Failed to parse chained task status', { category: 'SimulationOrchestrator', parseError });
          throw new Error('Invalid task status response format');
        }
      }
      
      if (parsedTaskStatus.status === 'completed' || parsedTaskStatus.status === 'COMPLETED' || parsedTaskStatus.task_status === 'COMPLETED') {
        results = parsedTaskStatus;
        break;
      } else if (parsedTaskStatus.status === 'failed' || parsedTaskStatus.status === 'FAILED' || parsedTaskStatus.task_status === 'FAILED') {
        throw new Error(`Simulation failed: ${parsedTaskStatus.message || 'Unknown error'}`);
      }
      
      await new Promise(r => setTimeout(r, 10000)); // 10 second intervals
      attempts++;
    }
    
    if (!results) {
      throw new Error('Simulation timed out - no results after 5 minutes');
    }

    loggingService.debug('✅ Chained simulation workflow completed!');
    return {
      scenarioId,
      timeWindowId: timeWindowBatchId,
      ridesBatchId,
      vehiclesBatchId: fleetId,
      problemId: problemConfigId,
      taskId,
      results
    };

  } catch (error: any) {
    loggingService.error('❌ Chained simulation workflow failed', { category: 'SimulationOrchestrator', error });
    return { error: error.message || 'Unknown error' };
  }
}

/**
 * Enhanced simulation workflow with better error handling and progress callbacks
 */
export async function runEnhancedSimulationWorkflow(
  config: ChainedSimulationWorkflowInput,
  onProgress?: (step: string, progress: number, message: string) => void
): Promise<ChainedSimulationWorkflowResult> {
  const updateProgress = (step: string, progress: number, message: string) => {
    loggingService.info(`[${progress}%] ${step}: ${message}`, { category: 'SimulationOrchestrator' });
    onProgress?.(step, progress, message);
  };

  try {
    updateProgress('Initialize', 0, 'Starting simulation workflow...');

    // Step 1: Upload Rides (0-15%)
    updateProgress('Upload Rides', 5, 'Uploading ride data...');
    const ridesRes = await uploadRides(config.rides, config.scenarioName);
    updateProgress('Upload Rides', 15, `Uploaded ${config.rides.length} rides`);

    // Step 2: Generate Fleet (15-30%)
    updateProgress('Generate Fleet', 20, 'Creating vehicle fleet...');
    
    // Check if fleet is already in API format (has vehicles with .vehicle property)
    const isAlreadyTransformed = config.fleet?.vehicles?.length > 0 && 
      config.fleet.vehicles[0]?.vehicle !== undefined;
    
    let transformedFleet;
    if (isAlreadyTransformed) {
      // Fleet is already in API format (from fleetMappers), use it directly
      // The dates should already be correct from fleetMappers which uses serviceDate
      transformedFleet = config.fleet;
      
      // Log if metadata is present for debugging
      if (config.fleet.uatMetadata?.serviceDate) {
        loggingService.debug('🔍 Using already-transformed fleet with serviceDate', {
          category: 'SimulationOrchestrator',
          serviceDate: config.fleet.uatMetadata.serviceDate,
        });
      } else {
        loggingService.debug('🔍 Using already-transformed fleet (no metadata found)');
      }
    } else {
      // Fleet needs transformation - pass fleet object with metadata if available
      const fleetObject = config.fleet 
        ? { vehicles: config.vehicles, uatMetadata: config.fleet.uatMetadata }
        : { vehicles: config.vehicles };
      transformedFleet = mapFleetToApiFleetSettings(fleetObject, [], config.rides[0]?.request_time);
    }
    const fleetRes = await generateFleet(transformedFleet, config.scenarioName);
    updateProgress('Generate Fleet', 30, `Created fleet with ${config.vehicles.length} vehicle types`);

    // Step 3: Calculate Time Windows (30-50%)
    updateProgress('Time Windows', 35, 'Calculating time windows...');
    const timeWindowConfig = {
      ...config.timeWindowSettings,
      ride_settings: {
        ...config.timeWindowSettings?.ride_settings,
        batch_id: ridesRes.batch_id
      }
    };
    const timeWindowRes = await calculateTimeWindowsWithUserAuth(timeWindowConfig, `${config.scenarioName}-time-window`);
    
    updateProgress('Time Windows', 40, 'Waiting for time window calculation...');
    const timeWindowResult = await pollTimeWindowCalculation(timeWindowRes.time_window_batch_id);
    updateProgress('Time Windows', 50, 'Time windows calculated successfully');

    // Step 4: Create Problem Config (50-60%)
    updateProgress('Problem Config', 55, 'Creating problem configuration...');
    const problemConfigRes = await createProblem(config.problemConfig);
    updateProgress('Problem Config', 60, 'Problem configuration created');

    // Step 5: Create Scenario (60-70%)
    updateProgress('Create Scenario', 65, 'Creating simulation scenario...');
    const scenarioPayload = {
      scenario_name: config.scenarioName,
      ride_batch_id: ridesRes.batch_id,
      time_window_batch_id: timeWindowResult.time_window_batch_id,
      fleet_id: fleetRes.fleet_id,
      problem_config_id: problemConfigRes.problem_id,
      timezone: config.simulationTimezone || config.fleet?.uatMetadata?.timezone || 'UTC',
    };
    const scenarioRes = await createScenario(scenarioPayload);
    updateProgress('Create Scenario', 70, 'Scenario created successfully');

    // Step 6: Run Simulation (70-80%)
    updateProgress('Run Simulation', 75, 'Starting simulation execution...');
    const simulationRes = await runSimulation(
      scenarioRes.scenario_id,
      'pasha_bulka',
      pashaRunSettingsFromRunBulk(config.run_bulk_simulation)
    );
    updateProgress('Run Simulation', 80, 'Simulation started, waiting for results...');

    // Step 7: Wait for Results (80-100%)
    let results = null;
    let attempts = 0;
    const maxAttempts = 30;
    
    while (attempts < maxAttempts) {
      const progressPercent = 80 + (attempts / maxAttempts) * 20;
      updateProgress('Get Results', progressPercent, `Polling for results (${attempts + 1}/${maxAttempts})...`);
      
      const taskStatus = await getTaskStatus(simulationRes.task_id);
      
      loggingService.debug(`🔍 Simulation status check (attempt ${attempts + 1})`, {
        category: 'SimulationOrchestrator',
        status: taskStatus.status,
      });
      
      // Handle case where taskStatus might be a string
      let parsedTaskStatus = taskStatus;
      if (typeof taskStatus === 'string') {
        loggingService.debug('⚠️ Simulation task status is a string, parsing...');
        try {
          parsedTaskStatus = JSON.parse(taskStatus);
        } catch (parseError) {
          loggingService.error('❌ Failed to parse simulation task status string', { category: 'SimulationOrchestrator', parseError });
          throw new Error('Invalid simulation task status response format');
        }
      }
      
      // Check for multiple completion status formats
      loggingService.debug('🔍 Simulation status parsing debug:');
      loggingService.debug('📊 parsedTaskStatus', {
        category: 'SimulationOrchestrator',
        status: parsedTaskStatus.status,
        task_status: parsedTaskStatus.task_status,
        full: JSON.stringify(parsedTaskStatus, null, 2),
      });
      
      const isCompleted = parsedTaskStatus.status === 'completed' || 
                         parsedTaskStatus.status === 'COMPLETED' ||
                         parsedTaskStatus.status === 'SUCCESS' || 
                         parsedTaskStatus.task_status === 'COMPLETED';
      
      const isFailed = parsedTaskStatus.status === 'failed' || 
                      parsedTaskStatus.status === 'FAILED' || 
                      parsedTaskStatus.task_status === 'FAILED';
      
      if (isCompleted) {
        loggingService.debug('✅ Simulation completed successfully!');
        results = parsedTaskStatus;
        break;
      } else if (isFailed) {
        loggingService.error('❌ Simulation failed', {
          category: 'SimulationOrchestrator',
          detail: parsedTaskStatus.errors_raised || parsedTaskStatus.message || 'Unknown error',
        });
        throw new Error(`Simulation failed: ${parsedTaskStatus.errors_raised || parsedTaskStatus.message || 'Unknown error'}`);
      }
      
      await new Promise(r => setTimeout(r, 10000));
      attempts++;
    }
    
    if (!results) {
      throw new Error('Simulation timed out');
    }

    updateProgress('Complete', 100, 'Simulation completed successfully!');

    return {
      scenarioId: scenarioRes.scenario_id,
      timeWindowId: timeWindowResult.time_window_batch_id,
      ridesBatchId: ridesRes.batch_id,
      vehiclesBatchId: fleetRes.fleet_id,
      problemId: problemConfigRes.problem_id,
      taskId: simulationRes.task_id,
      results
    };

  } catch (error: any) {
    updateProgress('Error', -1, `Simulation failed: ${error.message}`);
    loggingService.error('Enhanced simulation workflow failed', { category: 'SimulationOrchestrator', error });
    return { error: error.message || 'Unknown error' };
  }
}

// 720 attempts ≈ 60 min (20 × 3s + 700 × 5s) while the task is still running.
const TIME_WINDOW_POLL_ATTEMPTS = 720;
// Fail fast when the status check itself keeps failing (expired token, wrong task id, network down).
const MAX_CONSECUTIVE_POLL_FAILURES = 10;

// Polling function for time window calculation
export async function pollTimeWindowCalculation(taskId: string, maxAttempts: number = TIME_WINDOW_POLL_ATTEMPTS): Promise<{ time_window_batch_id: string }> {
  let attempts = 0;
  let consecutiveFailures = 0;
  
  while (attempts < maxAttempts) {
    try {
      loggingService.debug(`🔍 Polling time window calculation (attempt ${attempts + 1}/${maxAttempts})...`);
      const taskStatus = await getTaskStatus(taskId);
      consecutiveFailures = 0;
      
      loggingService.debug('📊 Time window task status', { category: 'SimulationOrchestrator', taskStatus });
      
      // Handle case where taskStatus might be a string
      let parsedTaskStatus = taskStatus;
      if (typeof taskStatus === 'string') {
        loggingService.debug('⚠️ Task status is a string, parsing...');
        try {
          parsedTaskStatus = JSON.parse(taskStatus);
        } catch (parseError) {
          loggingService.error('❌ Failed to parse task status string', { category: 'SimulationOrchestrator', parseError });
          throw new Error('Invalid task status response format');
        }
      }
      
      // Check for multiple completion status formats
      const actualStatus = parsedTaskStatus?.status || parsedTaskStatus?.data?.status;
      const actualTaskStatus = parsedTaskStatus?.task_status || parsedTaskStatus?.data?.task_status;
      const actualTaskResult = parsedTaskStatus?.task_result || parsedTaskStatus?.data?.task_result;
      
      loggingService.debug('🔍 Status parsing debug:');
      loggingService.debug('📊 Time window status parse', {
        category: 'SimulationOrchestrator',
        actualStatus,
        actualTaskStatus,
        full: JSON.stringify(parsedTaskStatus, null, 2),
      });
      
      // Note: CALCULATE_TIME_WINDOWS is a task/step name, not a completion status - do not treat as completed
      const isCompleted = actualStatus === 'completed' || 
                         actualStatus === 'COMPLETED' ||
                         actualStatus === 'SUCCESS' || 
                         actualTaskStatus === 'COMPLETED';
      
      const errorsRaised = parsedTaskStatus?.errors_raised ?? parsedTaskStatus?.data?.errors_raised;
      const hasErrorsRaised = errorsRaised && String(errorsRaised).trim().length > 0;
      const isFailed = actualStatus === 'failed' || 
                      actualStatus === 'FAILED' || 
                      actualTaskStatus === 'FAILED' ||
                      hasErrorsRaised;
      
      if (isCompleted) {
        loggingService.debug('✅ Time window calculation completed!');
        
        // Look for time window batch ID
        let timeWindowId = null;
        
        if (actualTaskResult && actualTaskResult.id) {
          timeWindowId = actualTaskResult.id;
        } else if (parsedTaskStatus?.time_window_batch_id) {
          timeWindowId = parsedTaskStatus.time_window_batch_id;
        } else if (parsedTaskStatus?.task_result?.id) {
          timeWindowId = parsedTaskStatus.task_result.id;
        } else if (parsedTaskStatus?.id) {
          timeWindowId = parsedTaskStatus.id;
        } else {
          timeWindowId = taskId;
        }
        
        loggingService.debug('🎯 Final time window batch ID', { category: 'SimulationOrchestrator', timeWindowId });
        return { time_window_batch_id: timeWindowId };
      } else if (isFailed) {
        const failMsg = String(errorsRaised || parsedTaskStatus.message || parsedTaskStatus.error || 'Unknown error');
        const isNoPathsFound = failMsg.includes('No paths found');
        const howToFix = isNoPathsFound
          ? 'Please double-check that the basemap is available and try again.'
          : 'Review the simulation input and try again. If the issue persists, contact support.';
        throw new Error(`Time window calculation failed: ${failMsg}. ${howToFix}`);
      }
      
      // Progressive wait times
      const waitTime = attempts < 20 ? 3000 : 5000;
      await new Promise(resolve => setTimeout(resolve, waitTime));
      attempts++;
    } catch (error) {
      // Rethrow task failure errors immediately - do not retry
      if (error instanceof Error && error.message.includes('Time window calculation failed')) {
        throw error;
      }
      loggingService.error(`Polling attempt ${attempts + 1} failed`, { category: 'SimulationOrchestrator', error });
      attempts++;
      consecutiveFailures++;
      
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        throw new Error(`Time window calculation status could not be checked (${consecutiveFailures} failed checks in a row)`);
      }
      if (attempts >= maxAttempts) {
        throw new Error(`Time window calculation timed out after about ${Math.round(maxAttempts / 12)} minutes`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  }
  
  throw new Error(`Time window calculation timed out after about ${Math.round(maxAttempts / 12)} minutes`);
}

// Fleet transformation utilities
export function mapFleetToApiFleetSettings(fleet: any, points: any[] = [], rideDate?: string): any {
  if (!fleet || !fleet.vehicles) {
    throw new Error('Invalid fleet configuration');
  }

  const getPointAddressById = (id: unknown): { latitude: number; longitude: number } | null => {
    if (typeof id !== 'string' || id.trim() === '') {
      return null;
    }

    const point = points.find((candidate: any) => candidate?.id === id);
    const coordinates = point?.coordinates;
    if (Array.isArray(coordinates) && coordinates.length === 2) {
      const [latitude, longitude] = coordinates;
      if (typeof latitude === 'number' && typeof longitude === 'number') {
        return { latitude, longitude };
      }
    }

    return null;
  };

  const getVehicleAddress = (
    shiftAddress: any,
    locationId: unknown,
    locationCoordinates: { lat?: number; lng?: number } | null | undefined
  ): { latitude: number; longitude: number } => {
    if (
      shiftAddress &&
      typeof shiftAddress.latitude === 'number' &&
      typeof shiftAddress.longitude === 'number'
    ) {
      return {
        latitude: shiftAddress.latitude,
        longitude: shiftAddress.longitude,
      };
    }

    const fromPoint = getPointAddressById(locationId);
    if (fromPoint) {
      return fromPoint;
    }

    if (
      locationCoordinates &&
      typeof locationCoordinates.lat === 'number' &&
      typeof locationCoordinates.lng === 'number'
    ) {
      return {
        latitude: locationCoordinates.lat,
        longitude: locationCoordinates.lng,
      };
    }

    return { latitude: 0, longitude: 0 };
  };

  // Determine base date for shifts (timezone-agnostic approach)
  // Priority: 1) Fleet metadata serviceDate, 2) Vehicle metadata serviceDate, 3) rideDate parameter, 4) default
  let baseDate: string;
  
  if (fleet.uatMetadata?.serviceDate) {
    // Use serviceDate from fleet metadata (from imported fleet)
    baseDate = extractServiceDate(String(fleet.uatMetadata.serviceDate));
    loggingService.debug('🔍 Using base date from fleet metadata (imported serviceDate)', { category: 'SimulationOrchestrator', baseDate });
  } else if (fleet.vehicles?.length > 0 && fleet.vehicles[0]?.uatMetadata?.serviceDate) {
    // Fallback to vehicle metadata serviceDate (from imported vehicle)
    baseDate = extractServiceDate(String(fleet.vehicles[0].uatMetadata.serviceDate));
    loggingService.debug('🔍 Using base date from vehicle metadata (imported serviceDate)', { category: 'SimulationOrchestrator', baseDate });
  } else if (rideDate) {
    // Use rideDate parameter if provided
    baseDate = extractServiceDate(String(rideDate));
    loggingService.debug('🔍 Using base date from rideDate parameter', { category: 'SimulationOrchestrator', baseDate });
  } else {
    // Last resort: use default date
    baseDate = DEFAULT_SIMULATION_DATE;
    loggingService.debug('🔍 Using default base date (no serviceDate found)', { category: 'SimulationOrchestrator', baseDate });
  }

  return {
    vehicles: fleet.vehicles.map((vehicleSettings: any) => {
      // Handle both UI Fleet format (direct Vehicle object) and API format (wrapped in vehicleSettings.vehicle)
      const vehicle = vehicleSettings.vehicle || vehicleSettings;
      const isUIFormat = !vehicleSettings.vehicle; // If no .vehicle property, it's UI format

      // Capture the human-readable vehicle name so we can register a per-shift
      // label later. Slot-driven flows (Postcode Stop Assignment, ELC) set
      // `vehicle.name` to e.g. "DAY 1 COACH"; other paths may carry the name on
      // the wrapping settings object or surface only an opaque id.
      const vehicleDisplayName: string | undefined =
        (typeof vehicle.name === 'string' && vehicle.name.trim() !== '' ? vehicle.name : undefined) ||
        (typeof vehicle.vehicle_name === 'string' && vehicle.vehicle_name.trim() !== '' ? vehicle.vehicle_name : undefined) ||
        (typeof vehicleSettings.name === 'string' && vehicleSettings.name.trim() !== '' ? vehicleSettings.name : undefined);

      // Route name / band for slot-driven flows (Postcode Stop Assignment, ELC).
      // Kept separate from `vehicleDisplayName` (the rego) so the route is
      // labelled "DAY 1" while the vehicle stays the rego. See section 4.7a of
      // the postcode user guide.
      const vehicleRouteName: string | undefined =
        typeof vehicle.routeName === 'string' && vehicle.routeName.trim() !== '' ? vehicle.routeName.trim() : undefined;
      const vehicleShiftBand: string | undefined =
        typeof vehicle.shiftBand === 'string' && vehicle.shiftBand.trim() !== '' ? vehicle.shiftBand.trim() : undefined;

      // Capture the emitted external_reference_id once so the label / route-meta
      // registrations key off the SAME id the simulator echoes on /vehicles
      // (previously this regenerated inline, leaving the vehicle-level join key
      // unregistered when the inbound vehicle had no external_reference_id).
      const vehicleExternalReferenceId = vehicle.external_reference_id || generateUUID();

      return {
        number_of_vehicles: vehicleSettings.number_of_vehicles || vehicleSettings.numberOfVehicles || 1,
        vehicle: {
          capacity: vehicle.capacity || (isUIFormat ? [
            { type: "standard", number: vehicle.standardSeats || 4 },
            { type: "wheelchair", number: vehicle.wheelchairSeats || 0 }
          ] : [
            { type: "standard", number: 4 },
            { type: "wheelchair", number: 0 }
          ]),
          // Smuggle the vehicle label / route meta through `brand` (a field HTTM
          // persists + echoes on /vehicles but never reads) so the names survive
          // reopen / reload, unlike the in-memory registry below. Falls back to
          // any inbound brand (e.g. an already-encoded fleetMappers payload).
          brand: encodeVehicleBrandLabel({
            name: vehicleDisplayName,
            routeName: vehicleRouteName,
            shiftBand: vehicleShiftBand,
          }) ?? vehicle.brand ?? "Default Brand",
          vehicle_type: vehicle.vehicle_type || "standard",
          external_reference_id: vehicleExternalReferenceId,
          shifts: (vehicle.shifts || (isUIFormat && vehicle.operatingHours ? vehicle.operatingHours.map((period: any) => ({
            start_time: period.start,
            end_time: period.end,
            start_address: period.start_address || {},
            end_address: period.end_address || {},
            breaks: period.breaks || []
          })) : []))?.map((shift: any, shiftIndex: number, allShifts: any[]) => {
            const startAddress = getVehicleAddress(
              shift.start_address,
              vehicle.startLocation,
              vehicle.startLocationCoordinates
            );
            const endAddress = getVehicleAddress(
              shift.end_address,
              vehicle.endLocation,
              vehicle.endLocationCoordinates
            );

            const startTime = shift.start_time ? normalizeTimeForFleetMapping(shift.start_time) : '06:00';
            const endTime = shift.end_time ? normalizeTimeForFleetMapping(shift.end_time) : '22:00';

            // Break locations must default to a real coordinate (the shift's start
            // address) rather than (0, 0) — Null Island is ~16,000km from any real
            // depot and makes the shift infeasible, which silently zeroes the
            // solver output.
            const fallbackBreakAddress = {
              latitude: startAddress.latitude,
              longitude: startAddress.longitude,
            };

            const parseBreakLocation = (raw: unknown): { latitude: number; longitude: number } => {
              if (typeof raw !== 'string' || raw.trim() === '') {
                return fallbackBreakAddress;
              }
              const [latStr, lngStr] = raw.split(',').map(s => s.trim());
              const lat = Number(latStr);
              const lng = Number(lngStr);
              if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
                return fallbackBreakAddress;
              }
              return { latitude: lat, longitude: lng };
            };

            const sanitizeBreakAddresses = (
              addresses: any
            ): Array<{ latitude: number; longitude: number }> => {
              if (!Array.isArray(addresses) || addresses.length === 0) {
                return [fallbackBreakAddress];
              }
              const valid = addresses
                .map((a: any) => ({
                  latitude: Number(a?.latitude),
                  longitude: Number(a?.longitude),
                }))
                .filter(a =>
                  Number.isFinite(a.latitude) &&
                  Number.isFinite(a.longitude) &&
                  !(a.latitude === 0 && a.longitude === 0)
                );
              return valid.length > 0 ? valid : [fallbackBreakAddress];
            };

            const externalDriverId = shift.external_driver_id || generateUUID();

            // Register a human-readable label so the simulation-results panel
            // can render "DAY 1 COACH" instead of the generic "Vehicle N"/index
            // fallback. The label is stashed client-side (the API does not
            // accept a `shift_name` field on the shift payload today); the
            // results panel looks it up by `vehicle_shift_id` / `shift_id` /
            // `vehicle_id`, all of which the simulator echoes from the ids we
            // send here.
            //
            // We can't predict which of our outgoing ids the simulator will
            // echo as `vehicle_shift_id` / `shift_id` / `vehicle_id`, so we
            // register the same label under every plausible echo candidate.
            // The registry de-dupes by key automatically.
            const shiftLabel = vehicleDisplayName
              ? allShifts.length > 1
                ? `${vehicleDisplayName} #${shiftIndex + 1}`
                : vehicleDisplayName
              : undefined;

            const echoKeys = [
              externalDriverId,
              vehicleExternalReferenceId,
              vehicle.external_reference_id,
              vehicle.id,
              shift.id,
              shift.shift_id,
              shift.external_driver_id,
            ];
            if (vehicleDisplayName && shiftLabel) {
              registerShiftLabel(echoKeys, shiftLabel);
            }
            // Route name / band travel the same join but stamp `route_name` /
            // `shift_band` (not the vehicle label), so the Fixed Route export
            // names the route "DAY 1" while the vehicle stays the rego.
            if (vehicleRouteName || vehicleShiftBand) {
              registerRouteMeta(echoKeys, { routeName: vehicleRouteName, shiftBand: vehicleShiftBand });
            }

            // Preserve any explicit shift_name already on the inbound shift (e.g. when a
            // fleetMappers-emitted payload is being re-transformed), otherwise prefer the
            // label we just resolved.
            const incomingShiftName: string | undefined =
              typeof shift.shift_name === 'string' && shift.shift_name.trim() !== ''
                ? shift.shift_name
                : undefined;
            const outgoingShiftName = incomingShiftName || shiftLabel;

            return {
              start_address: startAddress,
              end_address: endAddress,
              start_time: combineDateWithTime(baseDate, startTime),
              end_time: combineDateWithTime(baseDate, endTime),
              breaks: shift.breaks?.map((breakPeriod: any) => {
                // Transform BreakPeriod format to API format
                if (breakPeriod.start && breakPeriod.end) {
                  // VehicleCreator / shiftGenerator format: { start, end, location? }
                  return {
                    start_time: combineDateWithTime(baseDate, breakPeriod.start),
                    end_time: combineDateWithTime(baseDate, breakPeriod.end),
                    duration_in_seconds: calculateDurationInSeconds(breakPeriod.start, breakPeriod.end),
                    break_addresses: [parseBreakLocation(breakPeriod.location)],
                  };
                } else if (breakPeriod.start_time && breakPeriod.end_time) {
                  // Already in API format — normalise datetime and break_addresses
                  return {
                    ...breakPeriod,
                    start_time: combineDateWithTime(baseDate, normalizeTimeForFleetMapping(breakPeriod.start_time)),
                    end_time: combineDateWithTime(baseDate, normalizeTimeForFleetMapping(breakPeriod.end_time)),
                    break_addresses: sanitizeBreakAddresses(breakPeriod.break_addresses),
                  };
                } else {
                  // Default format
                  return {
                    start_time: combineDateWithTime(baseDate, '12:00'),
                    end_time: combineDateWithTime(baseDate, '13:00'),
                    duration_in_seconds: 3600,
                    break_addresses: [fallbackBreakAddress],
                  };
                }
              }) || [],
              external_driver_id: externalDriverId,
              // Trim 3: send shift_name on the payload so the sim echoes it back; registry remains as a fallback (Ben 2026-05-28).
              ...(outgoingShiftName ? { shift_name: outgoingShiftName } : {}),
            };
          }) || []
        }
      };
    })
  };
}

// Utility function for UUID generation
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

// Utility function to calculate duration in seconds from time strings (HH:mm format)
function calculateDurationInSeconds(startTime: string, endTime: string): number {
  const parseTime = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60;
  };
  
  const startSeconds = parseTime(startTime);
  const endSeconds = parseTime(endTime);
  
  // Handle overnight shifts
  return endSeconds >= startSeconds ? 
    endSeconds - startSeconds : 
    (24 * 3600) - startSeconds + endSeconds;
}

// Time window settings builder
export function buildTimeWindowSettings(
  timeWindowSlope: number,
  timeWindowIntercept: number, 
  timeWindowFixedWindow: number,
  onceOffProcessingTime: number,
  processingTimes: { seatType: string; processingTimeSeconds: number }[],
  scenarioName: string,
  maxTripTimeMinutes: number,
  rideBatchId: string
): {
  time_model_type: 'slope_and_intercept';
  settings: any;
  ride_settings: any;
} {
  const config = {
    time_model_type: 'slope_and_intercept' as const,
    settings: {
      slope: timeWindowSlope,
      intercept: timeWindowIntercept,
      window: timeWindowFixedWindow,
      max_time_minutes: maxTripTimeMinutes,
      once_off_processing_time_seconds: onceOffProcessingTime,
      processing_time: (processingTimes || []).map((pt: any) => ({
        seat_type: pt.seatType,
        processing_time_seconds: pt.processingTimeSeconds,
      })),
    },
    ride_settings: {
      rides_name: scenarioName || undefined,
      batch_id: rideBatchId
    }
  };
  
  loggingService.debug('🔍 Built time window config', { category: 'SimulationOrchestrator', config });
  return config;
}

// Simplified time window settings builder
export function buildSimpleTimeWindowSettings(rideBatchId: string, customSettings?: any): any {
  return buildTimeWindowSettings(
    customSettings?.slope || 2.0,
    customSettings?.intercept || 1200,
    customSettings?.window || 1200,
    customSettings?.once_off_processing_time_seconds || 15,
    [
      { seatType: 'standard', processingTimeSeconds: 30 },
      { seatType: 'wheelchair', processingTimeSeconds: 90 }
    ],
    'simulation-rides',
    customSettings?.max_time_minutes || 90,
    rideBatchId
  );
}


